"use client";

import { ArrowLeft, ArrowRight, Clock3, Radar } from "lucide-react";
import type { Post } from "@/types/post";
import { Link } from "@/i18n/navigation";
import Markdown from "@/components/markdown";
import MiroFishSiteFooter from "./site-footer";
import MiroFishSiteHeader from "./site-header";

const formatDate = (value?: string) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const estimateReadTime = (content?: string) => {
  const words = (content || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/[#>*_[\]-]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

  return Math.max(1, Math.ceil(words / 220));
};

const formatTagLabel = (tag: string) =>
  tag
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export default function MiroFishBlogDetailPage({
  post,
  relatedPosts,
}: {
  post: Post;
  relatedPosts: Post[];
}) {
  return (
    <div
      className="min-h-screen bg-[#04050a] text-white"
      data-testid="mirofish-blog-detail-page"
    >
      <MiroFishSiteHeader hashHrefPrefix="/" />

      <main id="main-content">
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-[12%] top-0 h-[360px] w-[360px] rounded-full bg-sky-500/16 blur-[120px]" />
            <div className="absolute right-[10%] top-[14%] h-[300px] w-[300px] rounded-full bg-orange-500/12 blur-[120px]" />
          </div>

          <div className="mx-auto max-w-7xl px-4 pb-16 pt-12 sm:px-6 lg:px-8 lg:pb-24 lg:pt-18">
            <div className="relative z-10 rounded-[36px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-6 shadow-[0_20px_120px_rgba(0,0,0,0.35)] sm:p-8 lg:p-10">
              <Link
                href="/blog"
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/62 transition hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Blog
              </Link>

              <div
                className="mt-8 rounded-[32px] border border-white/10 bg-black/25 p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] sm:p-8"
                data-testid="mirofish-blog-detail-hero"
              >
                <div className="flex flex-wrap gap-2">
                  {(post.tags ?? [])
                    .filter((tag) => tag.toLowerCase() !== "mirofish")
                    .map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/58"
                      >
                        {formatTagLabel(tag)}
                      </span>
                    ))}
                </div>

                <h1 className="mt-6 max-w-4xl text-balance text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl">
                  {post.title}
                </h1>
                <p className="mt-5 max-w-3xl text-lg leading-8 text-white/68">
                  {post.description}
                </p>

                <div className="mt-8 flex flex-wrap gap-5 text-sm text-white/52">
                  <span>{formatDate(post.created_at)}</span>
                  <span className="inline-flex items-center gap-2">
                    <Clock3 className="h-4 w-4" />
                    {estimateReadTime(post.content)} min read
                  </span>
                  {post.author_name ? <span>{post.author_name}</span> : null}
                </div>
              </div>

              <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                <article
                  className="rounded-[32px] border border-white/10 bg-black/25 p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] sm:p-8"
                  data-testid="mirofish-blog-detail-content"
                >
                  <div className="prose prose-invert max-w-none prose-headings:text-white prose-p:text-white/72 prose-li:text-white/72 prose-strong:text-white prose-a:text-sky-200">
                    {post.content ? <Markdown content={post.content} /> : null}
                  </div>
                </article>

                <aside className="space-y-6">
                  <div className="rounded-[28px] border border-white/10 bg-white/5 p-6">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/42">
                      Product CTA
                    </div>
                    <h2 className="mt-4 text-2xl font-semibold text-white">
                      Run the scenario with your own source material
                    </h2>
                    <p className="mt-3 text-sm leading-7 text-white/58">
                      Upload one report, memo, or narrative brief, then inspect
                      how the agent swarm rewrites the outcome.
                    </p>

                    <div className="mt-6 flex flex-col gap-3">
                      <Link
                        href="/#console"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-medium text-black transition hover:bg-white/90"
                      >
                        <Radar className="h-4 w-4" />
                        Start Engine
                      </Link>
                      <a
                        href="https://www.bilibili.com/video/BV1VYBsBHEMY/"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-medium text-white transition hover:bg-white/10"
                      >
                        Watch Demo
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    </div>
                  </div>

                  <div
                    className="rounded-[28px] border border-white/10 bg-white/5 p-6"
                    data-testid="mirofish-related-guides"
                  >
                    <div className="text-xs uppercase tracking-[0.24em] text-white/42">
                      Related Guides
                    </div>
                    <div className="mt-4 space-y-4">
                      {relatedPosts.map((item) => (
                        <Link
                          key={item.slug}
                          href={`/blog/${item.slug}` as any}
                          className="block rounded-[22px] border border-white/10 bg-black/20 p-4 transition hover:border-white/20 hover:bg-black/30"
                        >
                          <div className="text-xs uppercase tracking-[0.18em] text-white/42">
                            {formatDate(item.created_at)}
                          </div>
                          <h3 className="mt-2 text-base font-semibold text-white">
                            {item.title}
                          </h3>
                          <p className="mt-2 text-sm leading-6 text-white/56">
                            {item.description}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MiroFishSiteFooter />
    </div>
  );
}
